import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useChallengeDetail } from '../hooks/useChallengeDetail'
import { useSubmitProof, parseSubmitError } from '../hooks/useSubmitProof'
import { useWallet } from '../contexts/WalletContext'
import { getCurrentRound, CHALLENGE_STATE } from '../types/challenge'

function formatCountdown(seconds: number): string {
  if (seconds <= 0) return '마감됨'
  const d = Math.floor(seconds / 86400)
  const h = Math.floor((seconds % 86400) / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  if (d > 0)
    return `D-${d} ${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')} 남음`
}

function getRoundLabel(round: number, roundDeadlines: number[]): string {
  if (roundDeadlines.length < 2) return `${round + 1}라운드`
  const spacing = roundDeadlines[1] - roundDeadlines[0]
  return spacing >= 500_000 ? `${round + 1}주차` : `${round + 1}일차`
}

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif']

export default function SubmitProof() {
  const { address } = useParams<{ address: string }>()
  const { address: userAddress, isConnected, openConnectModal } = useWallet()
  const navigate = useNavigate()

  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [text, setText] = useState('')
  const [isDragging, setIsDragging] = useState(false)
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { data: challenge, isLoading } = useChallengeDetail(address ?? '', userAddress)
  const { mutate: submit, isPending } = useSubmitProof(address ?? '')

  const proofType = challenge?.proofType ?? 'photo'
  const currentRound = challenge ? getCurrentRound(challenge.roundDeadlines) : 0
  const roundDeadlines = challenge?.roundDeadlines ?? []
  const effectiveRound = Math.min(currentRound, Math.max(0, roundDeadlines.length - 1))
  const deadlineTs = roundDeadlines[effectiveRound] ?? 0

  useEffect(() => {
    if (!deadlineTs) return
    function tick() {
      setSecondsLeft(Math.max(0, deadlineTs - Math.floor(Date.now() / 1000)))
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [deadlineTs])

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview)
    }
  }, [preview])

  const isDeadlinePassed = secondsLeft !== null && secondsLeft <= 0

  function applyFile(f: File) {
    if (!ACCEPTED_TYPES.includes(f.type)) return
    setFile(f)
    if (preview) URL.revokeObjectURL(preview)
    setPreview(URL.createObjectURL(f))
  }

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      setIsDragging(false)
      const f = e.dataTransfer.files[0]
      if (f) applyFile(f)
    },
    [preview], // eslint-disable-line react-hooks/exhaustive-deps
  )

  function handleSubmit() {
    if (!isConnected) {
      openConnectModal()
      return
    }
    if (isDeadlinePassed) return
    if (proofType === 'photo' && !file) return
    if (proofType === 'text' && !text.trim()) return
    setSubmitError(null)
    submit(
      { proofType, round: currentRound, file: file ?? undefined, text },
      {
        onSuccess: () => void navigate(`/challenge/${address ?? ''}`),
        onError: (err) => setSubmitError(parseSubmitError(err)),
      },
    )
  }

  if (!address)
    return (
      <div className="empty">
        <p>잘못된 챌린지 주소입니다.</p>
      </div>
    )
  if (isLoading)
    return (
      <div className="empty">
        <div className="spinner spinner-lg" />
        <p>불러오는 중…</p>
      </div>
    )
  if (challenge && challenge.state !== CHALLENGE_STATE.ACTIVE)
    return (
      <div className="empty">
        <p>현재 인증을 제출할 수 없는 챌린지입니다.</p>
        <Link to={`/challenge/${address}`} className="btn btn-primary btn-sm">
          챌린지로 돌아가기
        </Link>
      </div>
    )
  if (challenge && !challenge.isParticipant)
    return (
      <div className="empty">
        <p>챌린지 참가자만 인증을 제출할 수 있습니다.</p>
        <Link to={`/challenge/${address}`} className="btn btn-primary btn-sm">
          챌린지로 돌아가기
        </Link>
      </div>
    )

  const roundLabel = challenge
    ? getRoundLabel(currentRound, challenge.roundDeadlines)
    : `${currentRound + 1}라운드`
  const hasContent =
    proofType === 'checkin' || (proofType === 'photo' ? Boolean(file) : text.trim().length > 0)
  const canSubmit = !isPending && !isDeadlinePassed && hasContent && isConnected
  const submitLabel = isPending
    ? '제출 중…'
    : proofType === 'checkin'
      ? '오늘 완료 체크 ✓'
      : '제출하기'

  return (
    <div style={{ maxWidth: 520, margin: '0 auto' }}>
      <Link
        to={`/challenge/${address}`}
        className="btn btn-quiet btn-sm"
        style={{ marginBottom: 16, paddingLeft: 0 }}
      >
        ← 뒤로
      </Link>

      {/* Header */}
      <div className="card card-pad" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 style={{ fontSize: 18, fontWeight: 700, margin: 0 }} data-testid="round-title">
            {roundLabel} 인증
          </h1>
          {secondsLeft !== null && (
            <span
              className={`badge ${isDeadlinePassed ? 'badge-danger' : 'badge-primary'} badge-lg`}
              data-testid="countdown"
            >
              {isDeadlinePassed ? '마감됨' : `마감까지 ${formatCountdown(secondsLeft)}`}
            </span>
          )}
        </div>
        {isDeadlinePassed && (
          <p style={{ fontSize: 13, color: 'var(--danger)', margin: '8px 0 0' }}>
            인증 마감 시각이 지나 제출이 불가합니다.
          </p>
        )}
      </div>

      {/* Submission body — varies by proof type */}
      <div className="card card-pad">
        {proofType === 'checkin' && (
          <div style={{ textAlign: 'center', padding: '12px 0 18px' }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>✅</div>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--ink-2)' }}>
              오늘 목표를 완료했다면 아래 버튼을 눌러 체크하세요.
            </p>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--ink-4)' }}>
              제출 기록은 온체인에 남습니다 (수정 불가).
            </p>
          </div>
        )}

        {proofType === 'text' && (
          <div className="field" style={{ marginBottom: 14 }}>
            <label className="field-label" htmlFor="proof-text">
              인증 내용
            </label>
            <textarea
              id="proof-text"
              className="textarea"
              style={{ minHeight: 120 }}
              placeholder="오늘 무엇을 했는지 적어주세요…"
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={2000}
              data-testid="proof-text"
            />
            <span className="field-hint">{text.length}/2000자</span>
          </div>
        )}

        {proofType === 'photo' && (
          <>
            {preview && (
              <div
                style={{
                  marginBottom: 14,
                  borderRadius: 10,
                  overflow: 'hidden',
                  border: '1px solid var(--border)',
                  position: 'relative',
                }}
              >
                <img
                  src={preview}
                  alt="미리보기"
                  style={{ width: '100%', maxHeight: 260, objectFit: 'cover', display: 'block' }}
                  data-testid="preview-img"
                />
                <button
                  onClick={() => {
                    setFile(null)
                    URL.revokeObjectURL(preview)
                    setPreview(null)
                  }}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: 'rgba(0,0,0,0.55)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 20,
                    padding: '4px 10px',
                    fontSize: 12,
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                >
                  제거
                </button>
              </div>
            )}

            <div
              style={{
                border: `2px dashed ${isDragging ? 'var(--primary)' : file ? 'var(--success)' : 'var(--border-2)'}`,
                borderRadius: 'var(--radius-lg)',
                padding: '36px 24px',
                textAlign: 'center',
                cursor: 'pointer',
                background: isDragging
                  ? 'var(--primary-weak)'
                  : file
                    ? 'var(--success-bg)'
                    : 'var(--surface-2)',
                transition: 'all .15s',
                marginBottom: 14,
              }}
              onDragOver={(e) => {
                e.preventDefault()
                setIsDragging(true)
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              data-testid="upload-area"
            >
              <span style={{ fontSize: 32, display: 'block', marginBottom: 8 }}>📁</span>
              <p style={{ margin: '0 0 4px', fontSize: 14, color: 'var(--ink-2)' }}>
                {file ? `선택됨: ${file.name}` : '클릭 또는 드래그앤드롭으로 이미지 선택'}
              </p>
              <p style={{ margin: 0, fontSize: 12, color: 'var(--ink-4)' }}>JPG / PNG / GIF 지원</p>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) applyFile(f)
              }}
              data-testid="file-input"
            />

            <div className="alert alert-warn" style={{ marginBottom: 14 }}>
              <span>⚠️</span>
              <div>
                제출 후 수정 불가. 목표와 관련된 사진만 업로드 (날짜·시각 확인 가능한 사진 권장)
              </div>
            </div>
          </>
        )}

        {submitError && (
          <div className="alert alert-danger" role="alert" style={{ marginBottom: 12 }}>
            {submitError}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <Link to={`/challenge/${address}`} className="btn btn-ghost">
            취소
          </Link>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid="submit-btn"
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
